import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, Circle, X, Rocket, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function OnboardingChecklist() {
  const [checklist, setChecklist] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/onboarding/checklist`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data && !res.data.dismissed) setChecklist(res.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const toggleTask = async (taskId, completed) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/onboarding/checklist/${taskId}`, { completed: !completed }, { headers: { Authorization: `Bearer ${token}` } });
      setChecklist(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.id === taskId ? { ...t, completed: !completed } : t),
        progress: {
          ...prev.progress,
          done: prev.progress.done + (completed ? -1 : 1),
          percent: Math.round(((prev.progress.done + (completed ? -1 : 1)) / prev.progress.total) * 100)
        }
      }));
    } catch { toast.error('Failed to update'); }
  };

  const dismiss = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/onboarding/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setChecklist(null);
      toast.success('Checklist dismissed');
    } catch {}
  };

  if (loading || !checklist || checklist.dismissed) return null;

  const { tasks, progress } = checklist;
  const categories = [...new Set(tasks.map(t => t.category))];
  const categoryLabels = { profile: 'Profile Setup', security: 'Security', channel: 'Channel Setup', data: 'Data Entry', task: 'Getting Started' };

  return (
    <div className="bg-gradient-to-r from-[hsl(var(--primary))]/10 to-violet-500/10 border border-[hsl(var(--primary))]/20 rounded-xl overflow-hidden mb-6" data-testid="onboarding-checklist">
      {/* Header */}
      <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-violet-500 flex items-center justify-center">
            <Rocket size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Welcome! Complete your setup</h3>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">{progress.done}/{progress.total} tasks done — {progress.percent}%</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Progress bar */}
          <div className="w-24 h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[hsl(var(--primary))] to-emerald-500 rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
          </div>
          <button onClick={(e) => { e.stopPropagation(); dismiss(); }} className="text-[hsl(var(--text-muted))] hover:text-white p-1"><X size={14} /></button>
          {expanded ? <ChevronUp size={14} className="text-[hsl(var(--text-muted))]" /> : <ChevronDown size={14} className="text-[hsl(var(--text-muted))]" />}
        </div>
      </div>

      {/* Tasks */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {categories.map(cat => (
            <div key={cat}>
              <p className="text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-1.5">{categoryLabels[cat] || cat}</p>
              <div className="space-y-1">
                {tasks.filter(t => t.category === cat).map(task => (
                  <button key={task.id} onClick={() => toggleTask(task.id, task.completed)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${task.completed ? 'bg-emerald-500/5 opacity-60' : 'hover:bg-white/5'}`}>
                    {task.completed ? <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" /> : <Circle size={18} className="text-[hsl(var(--text-muted))] flex-shrink-0" />}
                    <span className={`text-sm ${task.completed ? 'line-through text-[hsl(var(--text-muted))]' : ''}`}>{task.label}</span>
                    {task.required && !task.completed && <span className="text-[8px] text-red-400 font-semibold ml-auto">WAJIB</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          
          {progress.percent === 100 && (
            <div className="text-center py-3">
              <p className="text-sm font-medium text-emerald-400">Tahniah! Semua setup selesai!</p>
              <Button size="sm" variant="outline" onClick={dismiss} className="mt-2 text-xs">Dismiss Checklist</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
